/**
 * components/Icon/paths.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Every icon in SK Company Cash, as a monoline SVG path on a 16×16 grid.
 *
 * These paths are lifted verbatim from the design source
 * (design_handoff_company_cash/SK Company Cash.dc.html) — the design shipped
 * no icon assets, so the prototype drew its own. Copying the paths rather than
 * substituting an icon library is what keeps the nav visually identical.
 *
 * All of them are strokes, never fills: `fill="none"`, `stroke="currentColor"`,
 * round caps and joins. Stroke width varies by usage and is set by the caller
 * (1.3 for nav, 1.4 for chrome, 1.6 for the plus glyph).
 * ──────────────────────────────────────────────────────────────────────────
 */

export const ICON_PATHS = {
  /* ── Finance nav ─────────────────────────────────────────────────────── */
  /** Overview — a 2×2 grid of cells. */
  overview: 'M2.5 2.5h4.2v4.2H2.5zM9.3 2.5h4.2v4.2H9.3zM2.5 9.3h4.2v4.2H2.5zM9.3 9.3h4.2v4.2H9.3z',
  /** Assignment register — a list. */
  register: 'M2.5 4h11M2.5 8h11M2.5 12h6.5',
  /** Reconciliation — a balance scale. */
  recon: 'M8 2.5v11M3.5 5.5h9M3.5 5.5 2 9.5h3zM12.5 5.5 11 9.5h3z',
  /** Approvals — a check. */
  approvals: 'M3 8.6l3.2 3.2L13 5',
  /** Sync log — a retry loop. */
  router: 'M3 6.2a5 5 0 0 1 8.8-1.9M13 9.8a5 5 0 0 1-8.8 1.9M11.6 2.2v2.4H9.2M4.4 13.8v-2.4h2.4',
  /** Settlement alerts — someone leaving through a door. */
  settle: 'M9 2.5H4v11h5M11 5.5l3 2.5-3 2.5M7.2 8H14',
  /** Settings — sliders. */
  settings: 'M2.5 5.5h11M2.5 10.5h11M6 3.5v4M10.5 8.5v4',

  /* ── Approver nav ────────────────────────────────────────────────────── */
  queue: 'M3 8.6l3.2 3.2L13 5',
  decided: 'M2.5 4h11M2.5 8h11M2.5 12h6.5',
  rules: 'M2.5 5.5h11M2.5 10.5h11M6 3.5v4M10.5 8.5v4',

  /* ── Custodian nav ───────────────────────────────────────────────────── */
  /** My cash — a banknote. */
  mycash: 'M2.5 5h11v6.5h-11zM2.5 7.5h11M11 9.8h1.6',
  myledger: 'M2.5 4h11M2.5 8h11M2.5 12h6.5',

  /* ── Chrome ──────────────────────────────────────────────────────────── */
  /** Plus — the header's "Assign cash" glyph. Drawn at stroke-width 1.6. */
  plus: 'M8 3.2v9.6M3.2 8h9.6',
  /** Chevron pointing right — collapse the sidebar, and every row affordance. */
  chevronRight: 'M6 3.5 10.5 8 6 12.5',
  /** Chevron pointing left — expand the sidebar. */
  chevronLeft: 'M10 3.5 5.5 8 10 12.5',
  /** Close — the drawer and modal ×. */
  close: 'M4 4l8 8M12 4l-8 8',
  /** Search — the register's filter input. */
  search: 'M7.2 12.4a5.2 5.2 0 1 0 0-10.4 5.2 5.2 0 0 0 0 10.4zM11.2 11.2 14 14',
  /** Right arrow — the "how the data flows" strip separator. */
  arrowRight: 'M2.5 8h11M10 4.5 13.5 8 10 11.5',
} as const;

export type IconName = keyof typeof ICON_PATHS;
